import { AODA } from '@core/utilities/aoda';
import { Component, ElementRef, Injector, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { BaseComponent } from '@shared/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { SearchResultsCacheService } from '@shared/services/search-results-cache.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { MineralInventory } from '@shared/models/records/mineral-inventory.model';
import { DatabaseIndexNameType } from '@shared/models/common/database-index-name.model';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-mineral-inventory-record',
  templateUrl: './mineral-inventory-record.component.html',
  styleUrls: ['./mineral-inventory-record.component.scss'],
  encapsulation: ViewEncapsulation.None

})

export class MineralInventoryRecordComponent extends BaseComponent implements OnInit, OnDestroy {
    
    record: MineralInventory;
    searchTerms: string;
    geologyCommentList: any[] = [];
    lithologyCommentList: any[] = [];
    mineralizationCommentList: any[] = [];
  
    constructor(
      private injector: Injector,
      private route: ActivatedRoute,
      private el: ElementRef,
      private r2: Renderer2
    ) {
      super(injector);
    }
  
    ngOnInit() {
      super.ngOnInit();
      this.initRecord();
      this.loadOntarioExpandCollapse();
      this.fetchComments();
    }
  
    ngOnDestroy() {
      super.ngOnDestroy();
    }
  
    ngAfterViewInit(): void {
      AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
    }
  
    private initRecord() {
      if (!history.state.data) {
        this.record = JSON.parse(this.searchResultsCache.getRecord());
      } else {
        this.record = history.state.data;
        this.searchTerms = history.state.searchTerms;
        this.searchResultsCache.setRecord(this.record);
      }

      console.log("rec", this.record)
  
      const breadcrumb = {
        name: this.mdi_ident,
        prefix: "record.statusrecord",
        component: "MineralInventoryRecordComponent",
      };
      this.breadcrumbService.addBreadcrumb(breadcrumb);
    }
  
    get mdi_ident() {
      return this.record.mdi_ident;
    }
  
    get recordNames() {
      return this.record.all_names;
    }

    get amis_id() {
        return this.record.amis_id;
    }
    
    get intrusion_name() {
        return this.record.intrusion_name;
    }
  
    get depositStatus() {
      return this.record.deposit_status;
    }
  
    get related_deposit_flag() {
      return this.record.related_deposit_flag;
    }
  
    get realtedRecords() {
        return this.record.related_records;
    }
  
    get create_date() {
      return this.record.create_date;
    }
  
    get dateLastModified() {
      return this.record.updated_at;
    }

    get primary_commodities() {
        return this.record.primary_commodities;
    }

    get secondary_commodities() {
        return this.record.secondary_commodities;
    }

    get townships() {
      return this.record.townships;
    }

    get rgp_district() {
      return this.record.rgp_district;
    }

    get nts() {
        return this.record.nts;
    }

    get lat_dec() {
        return this.record.lat_dec;
    }

    get lng_dec() {
        return this.record.lng_dec;
    }

    get utm_easting() {
        return this.record.utm_easting;
    }

    get utm_northing() {
        return this.record.utm_northing;
    }

    get utm_zone() {
        return this.record.utm_zone;
    }

    get utm_datum() {
        return this.record.utm_datum;
    }

    get point_location_description() {
        return this.record.point_location_description;
    }

    get location_method() {
        return this.record.location_method;
    }

    get deposit_access_description() {
        return this.record.deposit_access_description;
    }

    get exploration_history() {
        return this.record.exploration_history;
    }

    get site_visits() {
        if (this.record.site_visits && this.record.site_visits.length > 0) {
          return true;
        }
        return false;
      }

    get assessment_works() {
      if (this.record.assessment_work && this.record.assessment_work.length > 0) {
        return true;
      }
      return false;
    }

    get province() {
        return this.record.province;
    }

    get subprovince() {
        return this.record.subprovince;
    }

    get terrane() {
        return this.record.terrane;
    }

    get domain() {
        return this.record.domain;
    }

    get belt() {
        return this.record.belt;
    }

    get tectonic_assemblage() {
        return this.record.tectonic_assemblage;
    }

    get geological_age() {
        return this.record.geological_age;
    }

    get metamorphism_type() {
        return this.record.metamorphism_type;
    }

    get metamorphism_grade() {
        return this.record.metamorphism_grade;
    }

    get formation_group() {
        return this.record.formation_group;
    }

    get supergroup() {
        return this.record.supergroup;
    }

    get geochronological_age() {
        return this.record.geochronological_age;
    }

    get geochronological_age_ref() {
        return this.record.geochronological_age_ref;
    }

    get contact_rgp() {
        return this.record.contact_rgp[0];
    }

    get assay_samples(): any[] {
        return this.record.assay_sample.sort((a, b) => (a.commodity[0].description > b.commodity[0].description ? 1 : -1));
    }

    get mineralZones() {
        if (this.record.mineral_zones && this.record.mineral_zones.length > 0) {
            return true;
        }
        return false;
    }

    get production() {
        if (this.record.production && this.record.production.length > 0) {
            return true;
        }
        return false;
    }

    get references() {
        if (this.record.references && this.record.references.length > 0) {
            return true;
        }
        return false;
    }

    get reserves() {
        if (this.record.reserves && this.record.reserves.length > 0) {
            return true;
        }
        return false;
    }

    get lithology() {
        if (this.record.lithology && this.record.lithology.length > 0) {
            return true;
        }
        return false;
    }

    get mineralization() {
        if (this.record.mineralization && this.record.mineralization.length > 0) {
            return true;
        }
        return false;
    }

    get assay_sample() {
        if (this.record.assay_sample && this.record.assay_sample.length > 0) {
            return true;
        }
        return false;
    }

        get classifications() {
        if (this.record.classifications && this.record.classifications.length > 0) {
            return true;
        }
        return false;
    }

    get characteristics() {
        if (this.record.characteristics && this.record.characteristics.length > 0) {
            return true;
        }
        return false;
    }

    get databaseIndex(): string {
        return DatabaseIndexNameType.mineralInventory;
    }

    get assessmentPersistentLink(): string {
        return environment.application_url + "persistent-linking?" + DatabaseIndexNameType.assessment + "=";
    }

    get publicationPersistentLink(): string {
        return environment.application_url + "persistent-linking?" + DatabaseIndexNameType.publication + "=";
    }

    fetchComments() {
        if(this.record.comments) {
            for(var i = 0; i < this.record.comments.length; i++) {
                var comment = this.record.comments[i];
                if (comment.subject_type == "GEOLOGY COMMENT") {
                    this.geologyCommentList.push(comment);
                } else if (comment.subject_type == "LITHOLOGY COMMENT") {
                    this.lithologyCommentList.push(comment);
                } else if (comment.subject_type == "MINERALIZATION COMMENT") {
                    this.mineralizationCommentList.push(comment);
                }
            }
        }
    }
  }